import { useEffect, useState } from 'react';
import { ApiError, cancelRegistration, confirmNewsletter, fetchNewsletter, fetchRoom, fetchWebinar, rescheduleRegistration, type Project, type RoomView, type SessionOffer } from '../api';
import { formatSession } from '../format';
import { Header } from './register';

/**
 * Die beiden Seiten hinter den Links in jeder Erinnerungsmail (Konzept 3.6):
 * „Ich kann doch nicht" und „Auf einen anderen Termin umbuchen". Beides über den
 * persönlichen Zugang, ohne neue Dateneingabe – und nie durch bloßes Öffnen des Links,
 * sondern erst nach einem Klick auf der Seite: Mailprogramme rufen Links gern von selbst auf.
 */

function useRoom(token: string): RoomView | null | undefined {
  const [view, setView] = useState<RoomView | null | undefined>(undefined);
  useEffect(() => {
    fetchRoom(token)
      .then(setView)
      .catch(() => setView(null));
  }, [token]);
  return view;
}

function Unknown({ project }: { project: Project }) {
  return (
    <>
      <Header project={project} />
      <main className="page page--message">
        <h1>Diesen Zugang gibt es nicht</h1>
        <p className="muted">Bitte prüfe den Link aus deiner E-Mail.</p>
      </main>
    </>
  );
}

export function CancelPage({ project, token }: { project: Project; token: string }) {
  const view = useRoom(token);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  if (view === undefined) return null;
  if (view === null) return <Unknown project={project} />;

  async function cancel() {
    setBusy(true);
    setError(false);
    try {
      await cancelRegistration(token);
      setDone(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  if (done || view.cancelled) {
    return (
      <>
        <Header project={project} />
        <main className="page">
          <h1>Abgesagt</h1>
          <div className="card">
            <p>Du bekommst zu diesem Termin keine Erinnerungen mehr.</p>
            <p className="muted">Wenn du es dir anders überlegst: Ein neuer Termin ist ein Klick, deine Daten sind noch da.</p>
            <p>
              <a className="button button--secondary" href={`/umbuchen/${token}`}>
                Anderen Termin wählen
              </a>
            </p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header project={project} />
      <main className="page">
        <h1>Doch nicht dabei?</h1>
        <div className="card">
          <p className="label">Dein Termin</p>
          <p style={{ fontSize: 20, fontWeight: 600 }}>{formatSession(view.session.startsAt, project.timezone)}</p>
          <p className="muted">{view.webinar.title}</p>
        </div>
        <div className="card">
          <p>Wenn du absagst, bekommst du keine Erinnerungen mehr. Deine Anmeldung bleibt gespeichert – du kannst jederzeit einen anderen Termin wählen.</p>
          {error && <div className="error">Das hat nicht geklappt. Bitte versuch es noch einmal.</div>}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button type="button" className="button" onClick={cancel} disabled={busy}>
              Ja, ich sage ab
            </button>
            <a className="button button--secondary" href={`/umbuchen/${token}`}>
              Lieber umbuchen
            </a>
          </div>
        </div>
      </main>
    </>
  );
}

export function ReschedulePage({ project, token }: { project: Project; token: string }) {
  const view = useRoom(token);
  const [offers, setOffers] = useState<SessionOffer[] | null>(null);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (!view) return;
    fetchWebinar(view.webinar.slug)
      .then((w) => {
        const others = w.sessions.filter((s) => s.startsAt !== view.session.startsAt);
        setOffers(others);
        if (others[0]) setChoice(others[0].startsAt);
      })
      .catch(() => setOffers([]));
  }, [view]);

  if (view === undefined) return null;
  if (view === null) return <Unknown project={project} />;

  async function submit() {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      const r = await rescheduleRegistration(token, choice);
      setDone(r.sessionStartsAt);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      setError(code === 'invalid_session' ? 'Dieser Termin wird nicht mehr angeboten. Bitte wähle einen anderen.' : 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const roomUrl = `${window.location.origin}/raum/${token}`;
    return (
      <>
        <Header project={project} />
        <main className="page">
          <h1>Umgebucht</h1>
          <div className="card card--brand">
            <p className="label label--brand">Dein neuer Termin</p>
            <p style={{ fontSize: 22, fontWeight: 600 }}>{formatSession(done, project.timezone)}</p>
            <p className="muted">{view.webinar.title}</p>
          </div>
          <div className="card">
            <p className="label">Dein Zugang bleibt derselbe</p>
            <p>
              <a href={roomUrl}>{roomUrl}</a>
            </p>
            <p className="muted small">Die Bestätigung mit Kalendereintrag kommt per E-Mail.</p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header project={project} />
      <main className="page">
        <h1>Anderen Termin wählen</h1>
        <div className="card">
          <p className="label">Bisher</p>
          <p style={{ fontWeight: 600 }}>{formatSession(view.session.startsAt, project.timezone)}</p>
          <p className="muted">{view.webinar.title}</p>
        </div>
        <div className="card">
          <p className="label">Neuer Termin</p>
          {offers === null && <p className="muted">Termine werden geladen …</p>}
          {offers?.length === 0 && <p className="muted">Derzeit wird kein anderer Termin angeboten.</p>}
          {offers && offers.length > 0 && (
            <div className="field">
              <select id="session" name="session" value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Neuer Termin">
                <option value="" disabled>
                  Bitte Termin auswählen
                </option>
                {offers.map((s) => (
                  <option key={s.startsAt} value={s.startsAt}>
                    {formatSession(s.startsAt, project.timezone)}
                    {s.kind === 'next_round' ? ' · nächste Vorführung' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {error && <div className="error">{error}</div>}
          <button type="button" className="button" onClick={submit} disabled={busy || !choice}>
            {busy ? 'Einen Moment …' : 'Umbuchen'}
          </button>
        </div>
      </main>
    </>
  );
}

/** Newsletter-Bestätigung (Double-Opt-in, Konzept 8.2): Einwilligung erst nach dem Klick auf der Seite. */
export function NewsletterPage({ project, token }: { project: Project; token: string }) {
  const [view, setView] = useState<{ email: string; confirmed: boolean } | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    fetchNewsletter(token)
      .then(setView)
      .catch(() => setView(null));
  }, [token]);

  if (view === undefined) return null;
  if (view === null) {
    return (
      <>
        <Header project={project} />
        <main className="page page--message">
          <h1>Diesen Bestätigungslink gibt es nicht</h1>
          <p className="muted">Bitte prüfe den Link aus deiner E-Mail.</p>
        </main>
      </>
    );
  }

  async function confirm() {
    setBusy(true);
    setError(false);
    try {
      await confirmNewsletter(token);
      setView((v) => (v ? { ...v, confirmed: true } : v));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Header project={project} />
      <main className="page">
        {view.confirmed ? (
          <>
            <h1>Danke, das war's</h1>
            <div className="card">
              <p>{view.email} bekommt ab jetzt den Newsletter von {project.name}.</p>
              <p className="muted small">Abbestellen kannst du ihn jederzeit über den Link am Ende jeder Ausgabe.</p>
            </div>
          </>
        ) : (
          <>
            <h1>Newsletter bestätigen</h1>
            <div className="card">
              <p>
                Möchtest du den Newsletter von {project.name} an <strong>{view.email}</strong> bekommen?
              </p>
              {error && <div className="error">Das hat nicht geklappt. Bitte versuch es noch einmal.</div>}
              <button type="button" className="button" onClick={confirm} disabled={busy}>
                Ja, ich möchte den Newsletter
              </button>
              <p className="muted small" style={{ marginTop: 12 }}>Wenn du das nicht warst, schließ die Seite einfach – dann passiert nichts.</p>
            </div>
          </>
        )}
      </main>
    </>
  );
}
